import { useEffect, useMemo, useState } from 'react'
import { Popover } from 'antd'
import { Icon } from '@iconify/react'

import {
    fetchProjectEpicOptions,
    getCachedProjectEpicOptions,
} from './epicOptionsCache'
import EpicPickerPanel from './EpicPickerPanel'

import styles from './epicSelector.module.css'

export { mapEpicListToOptions, getEpicDisplayOptionFromTask } from './epicUtils'
export { clearProjectEpicOptionsCache } from './epicOptionsCache'
export { default as EpicPickerPanel } from './EpicPickerPanel'

const findOptionByValue = (options, value) => {
    if (value == null || value === '' || !Array.isArray(options)) return null
    const needle = String(value)
    return options.find((item) => String(item?.value) === needle) || null
}

const EpicSelector = ({
    projectId,
    value,
    onChange,
    options: controlledOptions,
    displayOption,
    placeholder = 'Select epic',
    disabled = false,
    enabled = true,
    compact = false,
    className,
}) => {
    const [visible, setVisible] = useState(false)
    const [fetchedOptions, setFetchedOptions] = useState(
        () => getCachedProjectEpicOptions(projectId) || []
    )
    const [selectedOption, setSelectedOption] = useState(null)

    const isControlled = Array.isArray(controlledOptions)
    const options = isControlled ? controlledOptions : fetchedOptions

    useEffect(() => {
        if (isControlled || !enabled || !projectId) return undefined

        let cancelled = false
        const cached = getCachedProjectEpicOptions(projectId)
        if (cached?.length) {
            setFetchedOptions(cached)
        }

        fetchProjectEpicOptions(projectId)
            .then((list) => {
                if (!cancelled) setFetchedOptions(list || [])
            })
            .catch(() => {
                if (!cancelled && !cached?.length) setFetchedOptions([])
            })

        return () => {
            cancelled = true
        }
    }, [projectId, enabled, isControlled])

    useEffect(() => {
        if (!visible || isControlled || !enabled || !projectId) return undefined

        let cancelled = false
        fetchProjectEpicOptions(projectId, { force: true })
            .then((list) => {
                if (!cancelled) setFetchedOptions(list || [])
            })
            .catch(() => {})

        return () => {
            cancelled = true
        }
    }, [visible, projectId, enabled, isControlled])

    useEffect(() => {
        if (value == null || value === '') {
            setSelectedOption(null)
            return
        }
        setSelectedOption((prev) => {
            if (prev && String(prev.value) === String(value)) return prev
            return (
                findOptionByValue(options, value) ||
                (displayOption &&
                String(displayOption.value) === String(value)
                    ? displayOption
                    : null) ||
                prev
            )
        })
    }, [value, options, displayOption])

    const selectedItem = useMemo(() => {
        if (value == null || value === '') return null
        if (
            displayOption &&
            displayOption.value != null &&
            String(displayOption.value) === String(value)
        ) {
            return displayOption
        }
        if (selectedOption && String(selectedOption.value) === String(value)) {
            return selectedOption
        }
        return findOptionByValue(options, value)
    }, [value, displayOption, selectedOption, options])

    const handleSelect = (epicId, option) => {
        const next =
            option ||
            findOptionByValue(options, epicId) ||
            (selectedOption && String(selectedOption.value) === String(epicId)
                ? selectedOption
                : null)
        if (next) setSelectedOption(next)
        onChange?.(epicId)
        setVisible(false)
    }

    const panelContent = (
        <EpicPickerPanel
            active={visible}
            projectId={projectId}
            value={value}
            onChange={handleSelect}
            options={
                isControlled
                    ? controlledOptions
                    : fetchedOptions.length > 0
                      ? fetchedOptions
                      : undefined
            }
            placeholder={placeholder}
            disabled={disabled}
            enabled={enabled}
            showEpicKey={!compact}
        />
    )

    return (
        <Popover
            content={panelContent}
            trigger="click"
            placement="bottomLeft"
            open={disabled ? false : visible}
            onOpenChange={(open) => {
                if (!disabled) setVisible(open)
            }}
            arrow={false}
            styles={{
                root: { paddingTop: 4 },
                body: {
                    padding: 0,
                    background: 'transparent',
                    boxShadow: 'none',
                },
            }}
            getPopupContainer={() => document.body}
        >
            <button
                type="button"
                className={[
                    styles.trigger,
                    compact ? styles.triggerCompact : '',
                    className,
                ]
                    .filter(Boolean)
                    .join(' ')}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={visible}
            >
                <span className={styles.triggerInner}>
                    {selectedItem ? (
                        <span className={styles.triggerValue}>
                            {!compact && selectedItem.epicKey ? (
                                <span className={styles.triggerKey}>
                                    {selectedItem.epicKey}
                                </span>
                            ) : null}
                            <span className={styles.triggerTitle}>
                                {selectedItem.label}
                            </span>
                        </span>
                    ) : (
                        <span className={styles.triggerPlaceholder}>
                            {placeholder}
                        </span>
                    )}
                    {!compact ? (
                        <Icon
                            icon={
                                visible ? 'mdi:chevron-up' : 'mdi:chevron-down'
                            }
                            width={20}
                            height={20}
                            className={styles.chevron}
                        />
                    ) : null}
                </span>
            </button>
        </Popover>
    )
}

export default EpicSelector
